import { BoxSetting, BoxSettings } from '../api/graphql/master/types';
import { SubscriptionLineApiType } from '../types/api/SubscriptionApiType';
import { extractProductId } from './subscriptionDetail/utils';

type CheckResult = {
  satisfied: boolean;
  productIds: ProductIds;
  /** boxes 対象の契約ラインの数量合計 */
  totalQuantity: number;
};

type ProductIds = Record<string, SubscriptionLineApiType>;

export type BoxSettingResult = CheckResult & {
  setting: BoxSetting & { name: string };
};

export type BoxSettingResults = Record<string, BoxSettingResult>;

export type BoxSettingsSatisfied = { active: BoxSettingResults; passive: BoxSettingResults };

function checkMinQuantityWithSummary(
  lines: SubscriptionLineApiType[],
  bs: BoxSetting
): CheckResult {
  const { boxes, minQuantity } = bs;

  const targetProductIds: Record<string, boolean> = {};
  for (let i = 0; i < boxes.length; i++) {
    const { productId } = boxes[i];
    targetProductIds[productId] = true;
  }

  const productIds: ProductIds = {};
  let totalQuantity = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const id = extractProductId(line.productId);
    if (id && targetProductIds[id]) {
      totalQuantity += line.quantity;
      productIds[id] = line;
    }
  }

  const satisfied = totalQuantity >= minQuantity;
  return {
    satisfied,
    productIds,
    totalQuantity,
  };
}

/**
 * まとめ買い等の boxSettings について、契約ライン上にその box の商品が1本でもあるときは
 * minQuantity を満たしていることのみを検証する（該当商品が0本ならその box はスキップ）。
 * 送信直前の最新契約＋POST 内容をシミュレートしたラインに対して使う。
 */
export function boxMinQuantitiesSatisfiedForLines(
  lines: SubscriptionLineApiType[],
  boxSettings: BoxSettings
): boolean {
  for (const boxName in boxSettings) {
    const bs = boxSettings[boxName];
    const { satisfied, totalQuantity } = checkMinQuantityWithSummary(lines, bs);
    if (totalQuantity === 0) continue;
    if (!satisfied) return false;
  }
  return true;
}

export function satisfiedBoxSettings(
  lines: SubscriptionLineApiType[],
  categoryProductIds: Record<string, boolean>,
  bss: BoxSettings
): BoxSettingsSatisfied {
  // 母集団
  const active: BoxSettingResults = {};
  const passive: BoxSettingResults = {};

  for (const boxName in bss) {
    const bs = bss[boxName];
    const { productId } = bs;
    const { satisfied, productIds, totalQuantity } = checkMinQuantityWithSummary(lines, bs);
    if (satisfied) {
      active[productId] = {
        satisfied,
        productIds,
        totalQuantity,
        setting: { ...bs, name: boxName },
      };
    } else if (categoryProductIds[productId]) {
      passive[productId] = {
        satisfied,
        productIds,
        totalQuantity,
        setting: { ...bs, name: boxName },
      };
    }
  }

  return { active, passive };
}
